import React, { useEffect, useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
} from "recharts";
import { BarChart3, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { getProviderStats } from "../../services/providersApi";
import { useTheme } from "../../utils/ThemeContext";
import "./ProviderDashboardBox.css";

const COLOR_CURRENT = "#cc1e00";
const COLOR_PREVIOUS_DARK = "#9a9a9a";
const COLOR_PREVIOUS_LIGHT = "#8a8d91";
const CHANNEL_PALETTE = ["#cc1e00", "#0056b3", "#e67e22", "#27ae60", "#8e44ad", "#16a085", "#d4ac0d"];

const sumValues = (map) =>
    map && typeof map === "object"
        ? Object.values(map).reduce((acc, v) => acc + Number(v || 0), 0)
        : 0;

const prettifyChannel = (raw) => {
    if (!raw) return "Otro";
    const name = String(raw).trim();
    if (!name) return "Otro";
    return name.charAt(0).toUpperCase() + name.slice(1);
};

const GrowthBadge = ({ current, previous }) => {
    const growth =
        previous === 0
            ? current > 0
                ? 100
                : 0
            : ((current - previous) / previous) * 100;
    const isFlat = current === previous;
    const isUp = !isFlat && current > previous;
    const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
    const cls = isFlat ? "flat" : isUp ? "up" : "down";
    const label = isFlat ? "—" : `${isUp ? "+" : ""}${growth.toFixed(0)}%`;
    return (
        <span className={`pdb-growth pdb-growth--${cls}`}>
            <Icon size={12} />
            {label}
        </span>
    );
};

const ProviderDashboardBox = ({ providerId }) => {
    const { theme } = useTheme();
    const isLight = theme === "light";
    const axisColor = isLight ? "rgba(28,30,33,0.65)" : "rgba(255,255,255,0.65)";
    const tickColor = isLight ? "rgba(28,30,33,0.55)" : "rgba(255,255,255,0.55)";
    const gridColor = isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)";
    const cursorFill = isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.04)";
    const tooltipBg = isLight ? "#ffffff" : "#1f1f1f";
    const tooltipBorder = isLight ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(255,255,255,0.1)";
    const tooltipLabelColor = isLight ? "#1c1e21" : "#ffffff";
    const tooltipItemColor = isLight ? "#1c1e21" : "rgba(255,255,255,0.85)";
    const previousColor = isLight ? COLOR_PREVIOUS_LIGHT : COLOR_PREVIOUS_DARK;

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!providerId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await getProviderStats(providerId);
                if (!cancelled) setStats(data);
            } catch (err) {
                console.warn("Error cargando estadísticas del proveedor:", err);
                if (!cancelled) setError("No pudimos cargar tus métricas en este momento.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [providerId]);

    const weeklyData = useMemo(() => {
        if (!stats) return [];
        return [
            {
                name: "Vistas",
                actual: Number(stats.currentWeekProfileViews || 0),
                anterior: Number(stats.previousWeekProfileViews || 0),
            },
            {
                name: "Contactos",
                actual: Number(stats.currentWeekPhoneClicks || 0),
                anterior: Number(stats.previousWeekPhoneClicks || 0),
            },
            {
                name: "Enlaces",
                actual: sumValues(stats.currentWeekLinkClicks),
                anterior: sumValues(stats.previousWeekLinkClicks),
            },
            {
                name: "Fotos",
                actual: Number(stats.currentWeekWorkPhotoClicks || 0),
                anterior: Number(stats.previousWeekWorkPhotoClicks || 0),
            },
        ];
    }, [stats]);

    const channelsData = useMemo(() => {
        if (!stats || !stats.totalLinkClicks) return [];
        return Object.entries(stats.totalLinkClicks)
            .map(([k, v]) => ({ name: prettifyChannel(k), value: Number(v || 0) }))
            .filter((d) => d.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [stats]);

    const weeklyHasData = weeklyData.some((d) => d.actual > 0 || d.anterior > 0);

    return (
        <div className="profile-card provider-dashboard-card">
            <div className="profile-card-header">
                <h3 className="profile-subheading pdb-title">
                    <BarChart3 size={16} />
                    Panel de Control
                </h3>
                {stats?.weekStartDate && (
                    <span className="pdb-weekstart" title="Inicio de la semana actual">
                        Semana desde {new Date(stats.weekStartDate).toLocaleDateString("es-AR", {
                            day: "2-digit",
                            month: "short",
                        })}
                    </span>
                )}
            </div>

            <div className="profile-card-body">
                {loading ? (
                    <p className="pdb-muted">Cargando métricas...</p>
                ) : error ? (
                    <p className="pdb-muted">{error}</p>
                ) : (
                    <>
                        {/* Gráfico 1: Comparativa semanal */}
                        <section className="pdb-section">
                            <header className="pdb-section-header">
                                <h4 className="pdb-section-title">Esta semana vs. la anterior</h4>
                                <p className="pdb-section-sub">
                                    Cómo evolucionaron las interacciones de tu perfil en los últimos 7 días.
                                </p>
                            </header>

                            {!weeklyHasData ? (
                                <p className="pdb-muted pdb-empty">
                                    Todavía no hay interacciones registradas esta semana ni la anterior.
                                </p>
                            ) : (
                                <>
                                    <div className="pdb-chart" style={{ height: 240 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={weeklyData}
                                                margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                                <XAxis
                                                    dataKey="name"
                                                    stroke={axisColor}
                                                    fontSize={12}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    stroke={tickColor}
                                                    fontSize={11}
                                                    allowDecimals={false}
                                                    tickLine={false}
                                                />
                                                <Tooltip
                                                    cursor={{ fill: cursorFill }}
                                                    contentStyle={{
                                                        background: tooltipBg,
                                                        border: tooltipBorder,
                                                        borderRadius: 8,
                                                        fontSize: 12,
                                                        color: tooltipItemColor,
                                                    }}
                                                    labelStyle={{ color: tooltipLabelColor, fontWeight: 600 }}
                                                    itemStyle={{ color: tooltipItemColor }}
                                                />
                                                <Legend
                                                    wrapperStyle={{ fontSize: 12, paddingTop: 4, color: tickColor }}
                                                    iconType="circle"
                                                />
                                                <Bar
                                                    dataKey="anterior"
                                                    name="Semana anterior"
                                                    fill={previousColor}
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Bar
                                                    dataKey="actual"
                                                    name="Esta semana"
                                                    fill={COLOR_CURRENT}
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    <ul className="pdb-growth-grid">
                                        {weeklyData.map((d) => (
                                            <li key={d.name} className="pdb-growth-item">
                                                <span className="pdb-growth-label">{d.name}</span>
                                                <span className="pdb-growth-value">{d.actual}</span>
                                                <GrowthBadge current={d.actual} previous={d.anterior} />
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </section>

                        {/* Gráfico 2: Clicks por enlace */}
                        <section className="pdb-section">
                            <header className="pdb-section-header">
                                <h4 className="pdb-section-title">Clicks por enlace</h4>
                                <p className="pdb-section-sub">
                                    Qué canales externos están convirtiendo mejor (totales históricos).
                                </p>
                            </header>

                            {channelsData.length === 0 ? (
                                <p className="pdb-muted pdb-empty">
                                    Todavía no recibiste clicks en tus enlaces.
                                </p>
                            ) : (
                                <div
                                    className="pdb-chart"
                                    style={{ height: Math.max(160, channelsData.length * 44) }}
                                >
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={channelsData}
                                            layout="vertical"
                                            margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
                                        >
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                stroke={gridColor}
                                                horizontal={false}
                                            />
                                            <XAxis
                                                type="number"
                                                stroke={tickColor}
                                                fontSize={11}
                                                allowDecimals={false}
                                                tickLine={false}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                stroke={axisColor}
                                                fontSize={12}
                                                width={90}
                                                tickLine={false}
                                            />
                                            <Tooltip
                                                cursor={{ fill: cursorFill }}
                                                contentStyle={{
                                                    background: tooltipBg,
                                                    border: tooltipBorder,
                                                    borderRadius: 8,
                                                    fontSize: 12,
                                                    color: tooltipItemColor,
                                                }}
                                                labelStyle={{ color: tooltipLabelColor, fontWeight: 600 }}
                                                itemStyle={{ color: tooltipItemColor }}
                                                formatter={(value) => [value, "Clicks"]}
                                            />
                                            <Bar dataKey="value" name="Clicks" radius={[0, 6, 6, 0]}>
                                                {channelsData.map((_, i) => (
                                                    <Cell key={i} fill={CHANNEL_PALETTE[i % CHANNEL_PALETTE.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

export default ProviderDashboardBox;
